import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Business from "@mui/icons-material/Business";
import {
  Box,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useMatch } from "react-router-dom";
import {
  OLIVE_LOGO_SRC,
  SIDEBAR_COLLAPSED_WIDTH_PX,
  SIDEBAR_EXPANDED_WIDTH_PX,
} from "@/constants";

export function AdminSidebar() {
  const { t } = useTranslation("common");
  const [expanded, setExpanded] = useState(true);
  const [logoFailed, setLogoFailed] = useState(false);
  const tenantsMatch = useMatch({ path: "/tenants", end: true });

  const toggleExpanded = useCallback(() => {
    setExpanded((value) => !value);
  }, []);

  const widthPx = expanded ? SIDEBAR_EXPANDED_WIDTH_PX : SIDEBAR_COLLAPSED_WIDTH_PX;

  return (
    <Box
      component="nav"
      aria-label={t("sidebar.ariaLabel")}
      sx={{
        position: "relative",
        flexShrink: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        width: widthPx,
        transition: (theme) =>
          theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        background: "var(--theme-gradientBackground)",
        borderRight: "1px solid var(--theme-sidebar-border)",
      }}
    >
      <Box
        sx={{
          position: "relative",
          flexShrink: 0,
          minHeight: 64,
          display: "flex",
          alignItems: "center",
          px: expanded ? 1.5 : 0.5,
          pr: expanded ? 4 : 2.5,
          borderBottom: "1px solid var(--theme-sidebar-border)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            minWidth: 0,
            flex: 1,
            justifyContent: expanded ? "flex-start" : "center",
          }}
        >
          {logoFailed ? (
            <Box
              aria-hidden
              sx={{
                width: 32,
                height: 32,
                flexShrink: 0,
                border: "1px solid var(--theme-sidebar-border)",
                borderRadius: 1,
              }}
            />
          ) : (
            <Box
              component="img"
              src={OLIVE_LOGO_SRC}
              alt=""
              onError={() => {
                setLogoFailed(true);
              }}
              sx={{
                width: 32,
                height: 32,
                flexShrink: 0,
                objectFit: "contain",
              }}
            />
          )}
          {expanded ? (
            <Typography
              component="span"
              variant="subtitle1"
              noWrap
              sx={{ fontWeight: 600, color: "var(--theme-text-primary)" }}
            >
              {t("sidebar.brandName")}
            </Typography>
          ) : null}
        </Box>

        <IconButton
          size="small"
          onClick={toggleExpanded}
          aria-expanded={expanded}
          aria-label={expanded ? t("sidebar.collapse") : t("sidebar.expand")}
          sx={{
            position: "absolute",
            right: expanded ? 4 : -12,
            top: "50%",
            transform: "translateY(-50%)",
            bgcolor: "transparent",
            color: "var(--theme-text-secondary)",
            "&:hover": {
              bgcolor: "var(--theme-hover-secondary)",
              color: "var(--theme-text-primary)",
            },
          }}
        >
          {expanded ? <ChevronLeft fontSize="small" /> : <ChevronRight fontSize="small" />}
        </IconButton>
      </Box>

      <Divider
        sx={{
          borderColor: "var(--theme-sidebar-border)",
          "&::before, &::after": { borderColor: "var(--theme-sidebar-border)" },
        }}
      />

      <List
        dense
        component="div"
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          py: 1,
          px: expanded ? 1 : 0.5,
        }}
      >
        <Tooltip title={t("sidebar.tenants")} placement="right" disableHoverListener={expanded}>
          <ListItemButton
            component={NavLink}
            to="/tenants"
            sx={{
              borderRadius: 1,
              minHeight: 44,
              px: expanded ? 1.25 : 0.75,
              justifyContent: expanded ? "flex-start" : "center",
              ...(tenantsMatch
                ? {
                    color: "var(--theme-text-primary)",
                    bgcolor: "var(--theme-overlay-olive-15)",
                    boxShadow: "inset 4px 0 0 var(--theme-primary)",
                    "&:hover": {
                      bgcolor: "var(--theme-overlay-light-10)",
                      color: "var(--theme-text-primary)",
                      boxShadow: "inset 4px 0 0 var(--theme-primary)",
                    },
                  }
                : {
                    color: "var(--theme-text-secondary)",
                    bgcolor: "transparent",
                    "&:hover": {
                      bgcolor: "var(--theme-hover-secondary)",
                      color: "var(--theme-text-primary)",
                    },
                  }),
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: expanded ? 40 : 0,
                mr: expanded ? 1 : 0,
                justifyContent: "center",
                color: "inherit",
              }}
            >
              <Business fontSize="small" />
            </ListItemIcon>
            {expanded ? (
              <ListItemText
                primary={t("sidebar.tenants")}
                slotProps={{
                  primary: {
                    noWrap: true,
                    sx: { typography: "body2", fontWeight: 500, color: "inherit" },
                  },
                }}
              />
            ) : null}
          </ListItemButton>
        </Tooltip>
      </List>
    </Box>
  );
}
